import { useState } from "react";
import { Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import {
  TIER_PROGRAM_ROW_KIND_LABEL,
  type TierProgramRowKind,
} from "@colab/shared";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { formatApiError } from "@/lib/format-api-error";
import { useCreateTierRow } from "./use-create-row";

// Ports app/admin/tiers/[id]/_components/AddMatrixRowDialog.tsx. Adds a new
// matrix row scoped by kind (requirement vs benefit). Cells per tier are
// filled in the matrix editor after creation.

export function AddMatrixRowDialog({
  programId,
  kind,
  open,
  onOpenChange,
}: {
  programId: string;
  kind: TierProgramRowKind;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [label, setLabel] = useState("");
  const [description, setDescription] = useState("");
  const mutation = useCreateTierRow();

  function reset() {
    setLabel("");
    setDescription("");
  }

  function onChangeOpen(next: boolean) {
    if (!next) reset();
    onOpenChange(next);
  }

  function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const trimmedLabel = label.trim();
    if (!trimmedLabel) return;
    mutation.mutate(
      {
        programId,
        input: {
          row_kind: kind,
          label: trimmedLabel,
          description: description.trim() ? description.trim() : null,
        },
      },
      {
        onSuccess: () => {
          toast.success(`${TIER_PROGRAM_ROW_KIND_LABEL[kind]} added`);
          reset();
          onOpenChange(false);
        },
        onError: (err) => {
          toast.error(formatApiError(err, "Failed to add row"));
        },
      },
    );
  }

  const lower = TIER_PROGRAM_ROW_KIND_LABEL[kind].toLowerCase();

  return (
    <Dialog open={open} onOpenChange={onChangeOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">
            <Plus className="-mt-0.5 mr-1 inline h-5 w-5" />
            Add {lower}
          </DialogTitle>
          <DialogDescription>
            A new row in the matrix. Each tier gets a free-text cell you can
            fill in after the row is created.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="r-label">Label</Label>
            <Input
              id="r-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder={
                kind === "requirement"
                  ? "e.g. Total revenue, Leads per year"
                  : "e.g. Discount %, Demo assistance"
              }
              required
              maxLength={160}
              autoFocus
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="r-description">Description (optional)</Label>
            <Textarea
              id="r-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="A short note that appears on hover in the partner portal"
              rows={3}
              maxLength={500}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onChangeOpen(false)}
              disabled={mutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={mutation.isPending || !label.trim()}>
              {mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Add {lower}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
